import React, { useEffect } from 'react';
import { BarChart3, TrendingUp, Users, MessageSquare, BrainCircuit, Zap, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';

const MetricCard = ({ title, value, change, trend, icon: Icon, color }) => (
  <div className="bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div className={cn("p-3 rounded-2xl text-white", color)}>
        <Icon size={24} />
      </div>
      <div className={cn(
        "flex items-center gap-1 text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-widest",
        trend === 'up' ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50"
      )}>
        {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        {change}
      </div>
    </div>
    <h3 className="text-gray-400 font-bold text-xs uppercase tracking-widest mb-1">{title}</h3>
    <p className="text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
  </div>
);

const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
    }
  }, [user, navigate]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-10 text-center md:text-left">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">App Analytics</h1>
        <p className="text-gray-500 font-medium mt-1">Real-time performance metrics and user engagement data.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <MetricCard title="Daily Active Users" value="2.8k" change="18%" trend="up" icon={Users} color="bg-brand-indigo" />
        <MetricCard title="Avg Session Duration" value="24m" change="5%" trend="up" icon={Zap} color="bg-orange-500" />
        <MetricCard title="Interactions/User" value="12.4" change="3%" trend="down" icon={MessageSquare} color="bg-purple-500" />
        <MetricCard title="Room Success Rate" value="92%" change="7%" trend="up" icon={TrendingUp} color="bg-green-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900 tracking-tight">Popular Activity Types</h2>
            <BarChart3 size={20} className="text-gray-400" />
          </div>
          <div className="space-y-6">
            {[
              { label: 'Live Q&A', value: 45, color: 'bg-purple-500', icon: MessageSquare },
              { label: 'Brainstorming', value: 32, color: 'bg-green-500', icon: BrainCircuit },
              { label: 'Live Polls', value: 23, color: 'bg-brand-indigo', icon: BarChart3 },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <item.icon size={16} className="text-gray-400" />
                    <span className="text-sm font-bold text-gray-900">{item.label}</span>
                  </div>
                  <span className="text-xs font-bold text-gray-400">{item.value}%</span>
                </div>
                <div className="h-3 w-full bg-gray-50 rounded-full overflow-hidden">
                  <div className={cn("h-full rounded-full transition-all duration-1000", item.color)} style={{ width: `${item.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight mb-8">Recent Trends</h2>
          <div className="space-y-4">
            {[
              { label: 'Peak Usage Time', value: '2:00 PM - 4:00 PM', sub: 'UTC+1' },
              { label: 'Top Community', value: 'Web3 Builders', sub: '124 new members today' },
              { label: 'Retention Rate', value: '68%', sub: 'Last 30 days' },
              { label: 'Bounce Rate', value: '12%', sub: 'Platform-wide' },
            ].map((trend) => (
              <div key={trend.label} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl">
                <div>
                  <p className="text-xxs font-bold text-gray-400 uppercase tracking-widest">{trend.label}</p>
                  <p className="text-lg font-bold text-gray-900 tracking-tight">{trend.value}</p>
                </div>
                <p className="text-xs text-gray-500 font-bold">{trend.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
